/**
 * ADR-168 — `Add fields` publishes the next schema version.
 *
 * WHAT THIS PANEL IS FOR
 * ----------------------
 * A published schema version is IMMUTABLE (ADR-125 D4), so there is no such thing as editing
 * one. The only legal change is publishing v(N+1) whose every pre-existing node is byte-identical
 * and which adds optional fields — the canonical `additions_only_transition` rule is the
 * authority, and this panel's job is to make a candidate that rule can accept and to show the
 * operator exactly what they are about to make permanent.
 *
 * THREE THINGS THIS PANEL MUST NOT SOFTEN
 * --------------------------------------
 *  1. THE EXISTING SHAPE IS READ-ONLY, INCLUDING WHAT THIS FORM CANNOT EXPRESS. `enum` and
 *     `pattern` have no representation in `SchemaDraftSpec` — which is precisely why ADR-168 D3
 *     patches raw bytes rather than regenerating the document. Hiding them would leave the
 *     operator publishing an immutable version against a contract they could not see, so every
 *     keyword on every existing node is rendered as text.
 *  2. A NEW FIELD IS OFFERED THE CLOSED PERMITTED SET AND NOTHING ELSE — `type`, `description`,
 *     the two annotations `x-unit` and `x-unknown-sentinel`, plus `properties` on an `object`. `PERMITTED_ADDITION_KEYS` in
 *     `scripts/validate-reference-data.py` is the one rule; this form does not restate it as a
 *     second opinion, it simply offers no control for anything outside it.
 *  3. READINESS IS NOT VISIBILITY. ADR-168 D2 has three gates and the host surface owns two of
 *     them (`canPropose`, ownership). Whether the schema can be PARSED is the third, and it is
 *     refused when the panel is opened. A schema that will not parse is the case this action
 *     exists to repair, so hiding the control on it reproduces exactly the failure ADR-147's
 *     "gate on `klass`, not `editable`" ruling exists to prevent.
 *
 * The impact gate (Task 9) and the staying-consumers sentence (Task 10) attach to this panel;
 * the pinned-consumer read is already here because it is what both need, and it comes from the
 * EXISTING used-by endpoint — nothing else in this app computes a pin list.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { api, dataFindingLine, READINESS_ROUTE, SchemaPublishRefusedError, type DataImpactVerdict, type DataSetRow, type DataUsedBy, type SchemaPublishResult, type SchemaPublishVerdict } from '../api/client'
import { BreakingConsumerList } from './BreakingConsumerList'
import { ProposeToCoreAction } from './ProposeToCoreAction'
import type { FieldType } from './schemaDraft'
import { candidateSchema, defaultPublishOptions, nodeOf, pathOf, walkSchema, type AddableNode, type DraftAddition, type DraftChild, type ExistingField, type PublishOptions } from './schemaAddFieldsModel'
import { PendingConsumerList } from './PendingConsumerList'

/** `ADDITION_TYPES` in the canonical validator, in the order the create-only builder offers. */
const TYPES: FieldType[] = ['string', 'number', 'integer', 'boolean', 'object']

export interface SchemaAddFieldsProps {
  /** The SOURCE set the operator opened, e.g. `website-properties@v1`. The server selects the
   *  target version itself — the operator never types a number. */
  set: string
  /** The SCHEMA ref, e.g. `website-property@v1`. */
  schema: string
  /** The RAW schema document. Raw, not compiled: layer 2 lives in the `x-` keys, and the
   *  transition rule compares raw nodes. */
  schemaDocument: unknown
  /** ADR-168 D2's contract verdict. `denied` never reaches this panel — the host does not mount
   *  it — but `unavailable` does, and becomes the readiness refusal below. */
  publishState?: SchemaPublishVerdict['state']
  /** `null` on the wire whenever the verdict is `allowed` — the reads below are `||`-guarded,
   *  so the type says so rather than the callers stripping it. */
  publishReason?: string | null
  publishRoute?: string | null
  /** The set's ownership class, passed straight to the shared hand-off when the operator asks
   *  for an edit this action cannot make. `fleet_owned` is the default because it is the class
   *  every set this panel is mounted for has — the host gates on it before mounting. */
  klass?: DataSetRow['klass']
}

/**
 * THE FOUR EDITS THIS ACTION IS NOT (ADR-168 D9).
 *
 * Rename, remove, retype and constraint-change are all deferred, and the operator who wants one
 * arrives here because this is the only place in the editor that talks about the schema's shape.
 * So each is offered as a REQUEST and refused by name — the panel IS mounted for these, unlike a
 * contract-layer denial, which is why the refusal belongs inside it rather than beside it.
 *
 * The route is Task 7b's shared `ProposeToCoreAction`. Seeding a second hand-off here would give
 * the same refusal two destinations that drift apart.
 */
const OUT_OF_SCOPE = [
  { kind: 'rename', label: 'Rename an existing field', what: 'Renaming an existing field' },
  { kind: 'remove', label: 'Remove an existing field', what: 'Removing an existing field' },
  { kind: 'retype', label: 'Change an existing field\u2019s type',
    what: 'Changing an existing field\u2019s type' },
  { kind: 'constraint', label: 'Change a constraint on an existing field',
    what: 'Changing a constraint on an existing field' },
] as const

type OutOfScopeKind = typeof OUT_OF_SCOPE[number]['kind']

let nextAdditionId = 0

function blankAddition(parent: string): DraftAddition {
  nextAdditionId += 1
  return {
    id: `a${nextAdditionId}`,
    parent, name: '', type: 'string', description: '', unit: '', sentinel: '', children: [],
  }
}

/** The impact gate's state. `verdict` is set ONLY from a 200 that carried an explicit
 *  `blocks_propose`; every other outcome — a rejected request, or a 200 without the field —
 *  lands in `unavailable` and reads as unchecked. */
interface ImpactState {
  /** Which candidate this state describes, so a late answer for an older one is discarded. */
  key: string
  verdict?: DataImpactVerdict
  unavailable?: string
}

/**
 * WHAT THE ANNOTATIONS MEAN, where they are typed.
 *
 * These are semantic rules enforced by `validate-reference-data.py`, and their names carry almost
 * none of their meaning to an operator meeting them for the first time. The sentinel is the sharp
 * one: it encodes a DISTINCTION (unknown vs absent) rather than a value, it is only usable on a
 * type that cannot hold it, and a version published with the wrong one is immutable
 * (ADR-125 D4). So each rule is restated here in the operator's terms, and every example is one
 * the validator actually accepts.
 */
const ANNOTATION_HELP: Record<string, { title: string; body: string; examples: string[] }> = {
  'x-unit': {
    title: 'x-unit — what the number is measured in',
    body: 'Names the unit so a consumer never has to guess whether 0.12 is a ratio or a '
      + 'percent. The set is open — any non-empty string is accepted — so it is a convention '
      + 'the estate shares, not a fixed list. Leave it blank on a field that has no unit, '
      + 'which is most strings and every boolean.',
    examples: ['ratio — 0.12 means 12%', 'percent — 12 means 12%', 'usd', 'days', 'px'],
  },
  'x-unknown-sentinel': {
    title: 'x-unknown-sentinel — the value that means “we do not know”',
    body: 'Absent and unknown are different answers. A field that is absent was never asked; '
      + 'the sentinel is what a record carries when somebody looked and could not answer. It '
      + 'must be a single scalar, and it must be a value the field’s own type CANNOT hold: the '
      + 'validator compiles it into a choice between the declared type and this one value, and '
      + 'a sentinel that is also a valid value of that type matches both, which refuses every '
      + 'record carrying it. That is why a string field cannot have one at all.',
    examples: [
      '__GEORGE_QUESTION__ on a boolean — spartan\u2019s website-property@v1 uses exactly this',
      '__UNMEASURED__ on a number — no number is that text, so the two never collide',
      'a string field — not available, model the unknown state as its own boolean instead',
    ],
  },
}

/** The `?` beside an annotation, and the panel it opens. One row owns one open panel. */
function AnnotationHelp(props: { name: string; index: number; open: boolean; onToggle: () => void }) {
  const { name, index, open, onToggle } = props
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`what ${name} is for`}
      data-testid={`add-fields-help-${name}-${index}`}
      className="ml-1 w-4 h-4 rounded-full border border-gray-300 text-[10px] leading-none
        text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    >
      ?
    </button>
  )
}

/**
 * ONE addition's editor row.
 *
 * Extracted because it is the whole of the form and none of the panel: it reads the addition, the
 * offers it may join, and three callbacks, and it decides nothing. Everything the panel owns —
 * the refusals, the impact gate, the publish — stays out of here, so a change to a control is a
 * change to this component alone. Not exported: it has one call site, and a second export from
 * this module would also trip `react-refresh/only-export-components`.
 */
function SchemaAdditionRow(props: {
  /** The position in the list, which is what every `data-testid` here is keyed by. */
  index: number
  a: DraftAddition
  addable: AddableNode[]
  onPatch: (over: Partial<DraftAddition>) => void
  onPatchChild: (j: number, over: Partial<DraftChild>) => void
  onRemove: () => void
}) {
  const { index, a, addable, onPatch, onPatchChild, onRemove } = props
  // One at a time: two open panels would push the controls off the row, which is the thing the
  // collapsed help exists to avoid.
  const [help, setHelp] = useState<string | null>(null)
  const toggle = (name: string) => setHelp(cur => (cur === name ? null : name))
  const helpFor = ANNOTATION_HELP[help ?? '']
  return (
    <div className="border-t border-gray-100 py-2" data-testid={`add-fields-new-${index}`}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-600">
          in
          <select
            value={a.parent}
            onChange={e => onPatch({ parent: e.target.value })}
            data-testid={`add-fields-parent-${index}`}
            className="ml-1 border border-gray-200 rounded px-1 py-1 text-xs font-mono"
          >
            {addable.map(n => (
              <option key={n.path} value={n.path}>
                {n.path || '(the document root)'}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-600">
          name
          <input
            value={a.name}
            onChange={e => onPatch({ name: e.target.value })}
            aria-label="field name"
            data-testid={`add-fields-name-${index}`}
            className="ml-1 border border-gray-200 rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <select
          value={a.type}
          onChange={e => onPatch({ type: e.target.value as FieldType })}
          aria-label="field type"
          data-testid={`add-fields-type-${index}`}
          className="border border-gray-200 rounded px-1 py-1 text-sm"
        >
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          type="button"
          onClick={onRemove}
          data-testid={`add-fields-remove-${index}`}
          className="text-xs text-gray-400 hover:text-red-700"
        >
          remove
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <label className="text-xs text-gray-600 flex-1 min-w-40 flex items-center gap-1">
          description
          <input
            value={a.description}
            onChange={e => onPatch({ description: e.target.value })}
            aria-label="description"
            data-testid={`add-fields-description-${index}`}
            className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs"
          />
        </label>
        <label className="text-xs text-gray-600">
          x-unit
          <input
            value={a.unit}
            onChange={e => onPatch({ unit: e.target.value })}
            data-testid={`add-fields-x-unit-${index}`}
            className="ml-1 w-20 border border-gray-200 rounded px-1 py-1 text-xs"
          />
        </label>
        <AnnotationHelp
          name="x-unit" index={index}
          open={help === 'x-unit'} onToggle={() => toggle('x-unit')}
        />
        <label className="text-xs text-gray-600">
          x-unknown-sentinel
          <input
            value={a.sentinel}
            onChange={e => onPatch({ sentinel: e.target.value })}
            data-testid={`add-fields-x-unknown-sentinel-${index}`}
            className="ml-1 w-24 border border-gray-200 rounded px-1 py-1 text-xs"
          />
        </label>
        <AnnotationHelp
          name="x-unknown-sentinel" index={index}
          open={help === 'x-unknown-sentinel'} onToggle={() => toggle('x-unknown-sentinel')}
        />
      </div>

      {helpFor && (
        <div
          data-testid={`add-fields-help-${help}-${index}-panel`}
          role="note"
          className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600"
        >
          <p className="font-semibold text-gray-700">{helpFor.title}</p>
          <p className="mt-1">{helpFor.body}</p>
          <ul className="mt-1 space-y-0.5">
            {helpFor.examples.map(e => (
              <li key={e}>
                <code className="font-mono text-gray-700">{e}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
      {a.type === 'object' && (
        <div className="mt-2 pl-4 border-l-2 border-gray-100">
          {a.children.map((c, j) => (
            <div key={j} className="flex items-center gap-2 py-1">
              <input
                value={c.name}
                onChange={e => onPatchChild(j, { name: e.target.value })}
                aria-label="child name"
                data-testid={`add-fields-child-name-${index}-${j}`}
                className="border border-gray-200 rounded px-2 py-1 text-xs font-mono"
              />
              <select
                value={c.type}
                onChange={e => onPatchChild(j, { type: e.target.value as FieldType })}
                aria-label="child type"
                data-testid={`add-fields-child-type-${index}-${j}`}
                className="border border-gray-200 rounded px-1 py-1 text-xs"
              >
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {/* Offered so the refusal can be SPECIFIC. Removing the control would
                  make the rule invisible rather than satisfied. */}
              <label className="text-xs text-gray-600 inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={c.required}
                  onChange={e => onPatchChild(j, { required: e.target.checked })}
                  data-testid={`add-fields-child-required-${index}-${j}`}
                />
                required
              </label>
            </div>
          ))}
          <button
            type="button"
            onClick={() => onPatch({ children: [...a.children,
              { name: '', type: 'string' as FieldType, required: false }] })}
            data-testid={`add-fields-child-add-${index}`}
            className="text-xs text-indigo-700 hover:underline"
          >
            ＋ child
          </button>
        </div>
      )}
    </div>
  )
}

export function SchemaAddFields(props: SchemaAddFieldsProps) {
  const { set, schema, schemaDocument, publishState, publishReason, publishRoute,
    klass = 'fleet_owned' } = props

  const [open, setOpen] = useState(false)
  const [additions, setAdditions] = useState<DraftAddition[]>([])
  /** The published contract is reference, not the task — it opens on request. */
  const [showExisting, setShowExisting] = useState(false)
  const [usedBy, setUsedBy] = useState<DataUsedBy | null>(null)
  const [consumersError, setConsumersError] = useState<string | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [result, setResult] = useState<SchemaPublishResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [findings, setFindings] = useState<string[] | null>(null)
  /** The typed refusal itself, not only its message: `branch`, `landed` and `missing`
   *  are the parts an operator has to act on, and they live nowhere else. */
  const [refusal, setRefusal] = useState<SchemaPublishRefusedError | null>(null)
  const [impact, setImpact] = useState<ImpactState | null>(null)
  const [outOfScope, setOutOfScope] = useState<OutOfScopeKind | null>(null)
  const [options, setOptions] = useState<PublishOptions>(defaultPublishOptions)

  const { fields, addable } = useMemo(() => {
    const f: ExistingField[] = []
    const a: AddableNode[] = []
    walkSchema(schemaDocument, '', [], f, a)
    return { fields: f, addable: a }
  }, [schemaDocument])

  /**
   * The DEFAULT parent is the keyed collection's record, not the document root.
   *
   * A reference-data document's root properties are its binding and its owner; a field an
   * operator wants almost always belongs to the record. The root stays on offer because
   * nothing forbids a root-level addition — it is just not the thing to preselect.
   */
  const defaultParent = useMemo(
    () => addable.find(n => n.path.endsWith('[]'))?.path ?? addable[0]?.path ?? '',
    [addable])

  /**
   * THE READINESS REFUSAL — the third of D2's three gates, and the one that is NOT a visibility
   * condition. `unavailable` means the schema could not be read; an empty `addable` means it
   * parsed but carries no `properties` map, so `insertionPoint` has nowhere to splice.
   */
  const readiness = useMemo(() => {
    if (publishState === 'unavailable') {
      return {
        reason: publishReason
          || 'This set’s schema could not be read, so a next version cannot be derived from it.',
        route: publishRoute || READINESS_ROUTE,
      }
    }
    if (addable.length === 0) {
      return {
        reason: 'This set’s schema carries no `properties` map, so there is nowhere to add a '
          + 'field: an addition appears only under an existing one (ADR-168 D4).',
        route: publishRoute || READINESS_ROUTE,
      }
    }
    return null
  }, [publishState, publishReason, publishRoute, addable])

  // The pinned consumers, from the ONE endpoint that computes them (ADR-127 D4). Read on OPEN:
  // it is about the action, not about the page.
  useEffect(() => {
    if (!open || readiness) return
    let live = true
    setUsedBy(null)
    setConsumersError(null)
    api.getDataReferenceUsedBy(set)
      .then(d => { if (live) setUsedBy(d) })
      .catch((e: Error) => { if (live) setConsumersError(e.message || 'could not read usage') })
    return () => { live = false }
  }, [open, readiness, set])

  const existingPaths = useMemo(() => new Set(fields.map(f => f.path)), [fields])

  /**
   * WHAT THE FORM REFUSES, in the rule's own words where it has them.
   *
   * These are not a second validator: each is a refusal the canonical rule would answer as a
   * 422, restated where the operator is still typing. The rule stays the authority — publish is
   * disabled here, and the server refuses independently.
   */
  const refusals = useMemo(() => {
    const out: string[] = []
    const seen = new Set<string>()
    for (const [i, a] of additions.entries()) {
      const name = a.name.trim()
      if (!name) {
        // NUMBERED, because this is the one refusal that cannot name its subject. Every other
        // line below says which field it is about; an unnamed field has nothing to say, so two
        // blank rows produced the identical sentence twice and told the operator neither which
        // row was meant nor that there were two.
        out.push(`Field ${i + 1} needs a name.`)
        continue
      }
      const path = pathOf(a)
      if (existingPaths.has(path)) {
        out.push(`${name} already exists in this schema. A published version is immutable, so an `
          + 'existing field cannot be redefined (ADR-125 D4) — the route is to name it '
          + 'something else.')
      }
      if (seen.has(path)) out.push(`${path} is added twice.`)
      seen.add(path)
      if (a.children.some(c => c.required)) {
        out.push('An added object’s children may not be required: the object is optional, so a '
          + 'record that supplies it would be held to a constraint the addition did not '
          + 'announce (ADR-168 D4).')
      }
      if (a.type === 'object' && a.children.every(c => !c.name.trim())) {
        out.push(`${name} is an object with no children, so it can hold nothing.`)
      }
      if (a.sentinel.trim() && a.type === 'string') {
        // The validator compiles the annotation into `oneOf: [{type: 'string'}, {const: <s>}]`,
        // and `oneOf` demands exactly one match. Every string satisfies both branches, so a
        // record carrying the sentinel is refused. `SchemaBuilder` refuses this already, in
        // these words (`schemaDraft.ts`); the same rule reaches the operator the same way here.
        out.push(`"${name}" is a string, so an unknown-sentinel cannot be told apart from a `
          + 'real value — use it on a non-string field, or model the unknown state as its own '
          + 'field.')
      }
      if (a.type !== 'object' && a.children.length > 0) {
        out.push(`${name} is not an object, so it cannot have children.`)
      }
    }
    return out
  }, [additions, existingPaths])

  /**
   * THE IMPACT GATE (ADR-168 D7). Publish may proceed only on an HTTP 200 carrying an explicit
   * `blocks_propose` verdict for THIS candidate.
   *
   * `candidateKey` is what makes the second half of that sentence true. The check runs against a
   * specific document, and an operator who edits after a pass has a different document — so the
   * verdict is keyed by the candidate and a verdict for any other candidate is not a verdict
   * here. That closes the time-of-check/time-of-use gap in both directions: an edit removes the
   * verdict the moment it happens, and a late answer for a superseded candidate is discarded
   * rather than applied.
   */
  const candidate = useMemo(
    () => (additions.length === 0 || refusals.length > 0
      ? null
      : candidateSchema(schemaDocument, additions, addable)),
    [additions, refusals, schemaDocument, addable])
  const candidateKey = useMemo(
    () => (candidate === null ? '' : JSON.stringify(candidate)), [candidate])
  const checking = useRef<string | null>(null)

  useEffect(() => {
    if (!open || readiness || !candidateKey) {
      setImpact(null)
      return
    }
    let live = true
    setImpact(null)
    checking.current = candidateKey
    api.getDataImpact(set, { schema: JSON.parse(candidateKey) })
      .then(v => {
        if (!live) return
        // THE PRESENCE of the field is the gate, never the absence of `breaking`. A 200 whose
        // body carries no `blocks_propose` is the same non-answer as a 503.
        setImpact(v.blocks_propose === undefined
          ? { key: candidateKey, unavailable: v.detail || v.error || 'no verdict was reported' }
          : { key: candidateKey, verdict: v })
      })
      .catch((e: Error) => {
        // `client.ts` throws on any non-2xx, so both unavailable forms — the 503 that omits the
        // verdict field, and the 400 the checker's own exit 2 becomes — arrive here.
        if (live) setImpact({ key: candidateKey, unavailable: e.message || 'the request failed' })
      })
    return () => { live = false }
  }, [open, readiness, set, candidateKey])

  // A verdict for a candidate that no longer exists is not a verdict.
  const current = impact && impact.key === candidateKey ? impact : null
  const impactBreaking = current?.verdict?.blocks_propose === true
  const impactClean = current?.verdict?.blocks_propose === false
  const impactUnavailable = current?.unavailable

  const canPublish = additions.length > 0 && refusals.length === 0 && !publishing && !result
    && impactClean

  /** The repin ran when the server said anything about it. An absent key is "it did not run"
   *  — an older server, or the operator having unticked the box — never an empty result. */
  const repinReported = result !== null && (result.repinned !== undefined
    || result.left_behind !== undefined || result.deprecated !== undefined
    || result.deprecation_skipped !== undefined)

  const patch = (i: number, over: Partial<DraftAddition>) =>
    setAdditions(list => list.map((a, j) => (j === i ? { ...a, ...over } : a)))

  const patchChild = (i: number, j: number, over: Partial<DraftChild>) =>
    setAdditions(list => list.map((a, ai) => (ai !== i ? a : {
      ...a, children: a.children.map((c, ci) => (ci === j ? { ...c, ...over } : c)),
    })))

  const onPublish = async () => {
    if (!canPublish) return
    setPublishing(true)
    setError(null)
    setFindings(null)
    setRefusal(null)
    try {
      // `undefined` rather than `true` for the default: the endpoint reads an absent `repin`
      // as the move, so the untouched form sends no opinion about it at all.
      const res = await api.publishSchemaVersion(
        set, additions.map(a => ({ path: pathOf(a), ...nodeOf(a) })),
        options.repin ? undefined : false)
      setResult(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      // EVERY finding goes through `dataFindingLine`. A finding is an object on the wire, and
      // rendered as a React child it takes the whole area down with error #31 — the crash that
      // helper exists to remove. A second render path here would reintroduce it.
      if (e instanceof SchemaPublishRefusedError) {
        setRefusal(e)
        if (e.findings) setFindings(e.findings.map(dataFindingLine))
      }
    } finally {
      setPublishing(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="add-fields-open"
        className="text-xs text-indigo-700 hover:underline"
      >
        Add fields
      </button>
    )
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4" data-testid="add-fields">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Add fields to <code>{schema}</code>
        </h3>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="add-fields-close"
          className="text-xs text-gray-500 hover:underline"
        >
          Close
        </button>
      </div>

      {readiness ? (
        /* A READINESS refusal, shown on open — not a hidden control. This is the malformed
           schema the action exists to repair, so the operator reaches a route rather than an
           absence they cannot interpret.

           AND THE ROUTE IS THE SHARED CONTROL, not prose. `handOffAgentFor` maps `unavailable`
           to `fleet-architect`, so this refusal HAS a destination — rendering it as a dead-end
           sentence would be the failure ADR-121 D1 exists to prevent, and a third copy of a
           rendering ADR-168 D10 already unified. `publishState` is pinned to `unavailable` for
           both readiness cases: a schema with no `properties` map is the same repair job as one
           that would not parse, and it is the fleet's own capability that repairs it. */
        <div className="text-sm text-amber-800" data-testid="add-fields-refused" role="alert">
          <ProposeToCoreAction
            set={set}
            schema={schema}
            klass={klass}
            reason={readiness.reason}
            route={readiness.route}
            publishState="unavailable"
          />
        </div>
      ) : (
        <>
          {/* THE INTRO, AND WHY IT NO LONGER PROMISES THAT NOTHING MOVES.
              It used to say "every existing pin keeps the version it names". With the move
              ticked by default that is false for every fleet-owned consumer, so the promise is
              gone and the FACT UNDERNEATH IT stays: ADR-168 D2 permits optional additions only
              and the data document is cloned byte-for-byte, so at publish time the next
              version resolves exactly the values this one does. That is what makes moving a
              consumer onto it safe, and it is true whichever way the box below sits. */}
          <p className="text-xs text-gray-500 mb-3" data-testid="add-fields-intro">
            This publishes the <strong>next version</strong> of the schema and a copy of the
            data. The copy carries today’s values byte-for-byte and the schema only gains
            optional fields, so the new version resolves the same values this one does —
            which is what makes moving a consumer onto it safe. Nothing already published is
            rewritten; what a publish can change is which version each fleet-owned consumer
            names, and only while the move below is ticked.
          </p>

          {/* THE OUT-OF-SCOPE REQUEST. Refused by name, and routed — see `OUT_OF_SCOPE`. */}
          <div className="mb-4" data-testid="add-fields-out-of-scope">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Need one of these instead?
            </p>
            <div className="flex flex-wrap gap-2">
              {OUT_OF_SCOPE.map(o => (
                <button
                  key={o.kind}
                  type="button"
                  onClick={() => setOutOfScope(o.kind)}
                  data-testid={`add-fields-out-of-scope-${o.kind}`}
                  aria-pressed={outOfScope === o.kind}
                  className={`text-xs px-2 py-1 rounded border ${outOfScope === o.kind
                    ? 'border-gray-400 bg-gray-100 text-gray-800'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {outOfScope && (
              <div className="mt-2" data-testid="add-fields-out-of-scope-refused" role="alert">
                <ProposeToCoreAction
                  set={set}
                  schema={schema}
                  klass={klass}
                  reason={`${OUT_OF_SCOPE.find(o => o.kind === outOfScope)!.what} is not `
                    + `something this action can do. Every field already in ${schema} is `
                    + 'published and therefore immutable (ADR-125 D4), and the next version may '
                    + 'only ADD optional fields — so there is no step here that would carry the '
                    + 'existing pins onto a changed shape, and no such step exists anywhere in '
                    + 'the editor yet (ADR-168 D9).'}
                  route="Ask core to change this schema"
                  usedBy={usedBy ?? undefined}
                />
              </div>
            )}
          </div>

          {/* NEW FIELDS. Only the permitted keys have a control at all. */}
          <div className="mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              New optional fields
            </p>
            {additions.map((a, i) => (
              <SchemaAdditionRow
                key={a.id}
                index={i}
                a={a}
                addable={addable}
                onPatch={over => patch(i, over)}
                onPatchChild={(j, over) => patchChild(i, j, over)}
                onRemove={() => setAdditions(list => list.filter((_, j) => j !== i))}
              />
            ))}
            <button
              type="button"
              onClick={() => setAdditions(list => [...list, blankAddition(defaultParent)])}
              data-testid="add-fields-add"
              className="text-xs text-indigo-700 hover:underline mt-2"
            >
              ＋ Add a field
            </button>
          </div>

          {refusals.length > 0 && (
            <ul className="text-sm text-red-700 mb-3" data-testid="add-fields-form-refused" role="alert">
              {refusals.map((r, i) => <li key={`${i}:${r}`}>{r}</li>)}
            </ul>
          )}

          {/* WHO PINS THIS SET, AND WHAT HAPPENS TO THEM (ADR-168 D5).
              Naming them is the point: a count does not tell the operator whose pin they are
              about to move, or to leave on the old version. The block follows the checkbox
              because the two answers are different sentences, and a block that described only
              one of them would be false half the time. The value fact is stated either way —
              the data document is cloned byte-for-byte, so the version a consumer resolves
              carries the same values whether it moved or stayed. ADR-168 D8 defers the CI
              guard behind that promise by owner ruling, so this is the only place it is
              stated.

              The list is the used-by read above — the one endpoint that computes a pin list
              (ADR-127 D4). Nothing here recomputes it. */}
          <div className="text-xs text-gray-600 mb-3" data-testid="add-fields-consumers">
            {consumersError ? (
              <span className="text-amber-800">
                The pinned-consumer list could not be read: {consumersError}. This is{' '}
                <strong>not</strong> “nothing pins this set”.
              </span>
            ) : usedBy === null ? (
              'Reading who pins this set…'
            ) : usedBy.consumers.length === 0 ? (
              `Nothing pins ${set}.`
            ) : options.repin ? (
              <>
                These pin <code className="font-mono">{set}</code> today, and publishing{' '}
                <strong>moves</strong> each one this fleet owns onto the new version:{' '}
                {usedBy.consumers.map(c => `${c.kind} ${c.name}`).join(', ')}. The copy carries
                today’s values byte-for-byte, so each one that moves keeps resolving exactly
                the values it was validated against. A core-owned consumer is left where it is,
                and the result below names it.
              </>
            ) : (
              <>
                These stay on <code className="font-mono">{set}</code> and{' '}
                <strong>keep its values</strong>, not just its shape:{' '}
                {usedBy.consumers.map(c => `${c.kind} ${c.name}`).join(', ')}. Publishing the next
                version copies today’s values into it and leaves{' '}
                <code className="font-mono">{set}</code> untouched, so each of these keeps
                resolving exactly the values it was validated against.
              </>
            )}
          </div>

          {/* THE IMPACT GATE. Three outcomes, and only one of them permits a publish. */}
          {candidateKey && (
            <div className="text-xs mb-3">
              {impactBreaking ? (
                <div className="text-red-700" data-testid="add-fields-impact-breaking" role="alert">
                  <p className="font-medium">
                    This next version breaks {current?.verdict?.breaking?.length ?? 0} pinned
                    consumer(s), so it cannot be published.
                  </p>
                  {/* One rendering, shared with the Data area's record edit. This panel used
                      to print nothing for a row with no located line, which is the drift the
                      shared component removes. */}
                  <BreakingConsumerList rows={current?.verdict?.breaking} className="mt-1" />
                  <PendingConsumerList rows={current?.verdict?.pending} className="text-xs mt-2" />
                </div>
              ) : impactUnavailable ? (
                /* NOT a pass. The 503 omits the verdict on purpose, the 400 is the checker's own
                   "could not look", and a 200 without the field is the same non-answer. Reading
                   any of them as clean is exactly how the gate becomes decorative. */
                <div className="text-amber-800" data-testid="add-fields-impact-unchecked" role="alert">
                  <p>
                    The consumer impact of this next version is <strong>unchecked</strong>:{' '}
                    {impactUnavailable}. <strong>This is not “nothing would break”</strong> — no
                    verdict was reported, so there is nothing here to publish on.
                  </p>
                </div>
              ) : impactClean ? (
                /* The wording is deliberate. D7's fourth limit is that consumer discovery can be
                   incomplete and a short scan is indistinguishable from a clean one, so this
                   reports what the check FOUND and never promises what will happen. */
                <>
                  <p className="text-gray-600" data-testid="add-fields-impact">
                    No breaking consumer was found among the{' '}
                    {current?.verdict?.consumers_checked ?? 0} checked. This is the checker&rsquo;s
                    finding, not a guarantee. It was asked about the candidate this panel assembled
                    from the additions above, not about the bytes the server splices (ADR-168 D3),
                    so the server re-runs this check on the real document before anything is
                    proposed.
                  </p>
                  {/* CLEAN IS NOT THE WHOLE ANSWER, and this is the branch that would otherwise
                      say it was. `blocks_propose` follows `breaking` alone, so a version that
                      breaks nothing can still leave consumers referencing data nobody has
                      committed. Publishing stays open — the producer here is not who commits that
                      data — and the sequencing is the part the author needs. */}
                  <PendingConsumerList rows={current?.verdict?.pending} className="text-xs mt-2" />
                </>
              ) : (
                <p className="text-gray-500" data-testid="add-fields-impact-checking">
                  Checking what this next version would do to the consumers that pin{' '}
                  <code className="font-mono">{set}</code>…
                </p>
              )}
            </div>
          )}

          {/* THE REPIN (ADR-180 D10). One checkbox and one sentence, ticked on arrival — the
              move is what the operator wants by default, and the alternative is every consumer
              left pinned to the version this publish replaces.

              NO COUNT AND NO EXCEPTIONS LIST. `impact_of_change` reports `consumers_checked` as
              a bare integer and carries neither paths nor ownership, so "4 will move, 1 stays"
              is not something this panel can compute — and the number it could show would
              include the core-owned consumers that do not move. The panel states the INTENT;
              the result states what happened — ADR-180 D10 decides both halves of that. */}
          <label
            className="flex items-start gap-2 text-xs text-gray-600 mb-3"
            data-testid="add-fields-repin-label"
          >
            <input
              type="checkbox"
              checked={options.repin}
              onChange={e => setOptions(o => ({ ...o, repin: e.target.checked }))}
              data-testid="add-fields-repin"
              className="mt-0.5"
            />
            <span>
              <strong className="font-medium text-gray-800">
                Move consumers to the new version
              </strong>
              <br />
              Core-owned consumers stay where they are, and the result names each one that was
              left behind.
            </span>
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onPublish}
              disabled={!canPublish}
              data-testid="add-fields-publish"
              className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-40"
            >
              {publishing ? 'Publishing…' : 'Publish the next version'}
            </button>
            {result && (
              <span className="text-xs text-green-700" data-testid="add-fields-published">
                Proposed <code>{result.schema_ref}</code> — nothing changes for the consumers
                above until it lands.
              </span>
            )}
          </div>

          {/* WHERE TO GO NEXT, AND ONLY WHAT IS TRUE.
              `improvement_id` is the row that puts this PR on the Improvements board (ADR-079),
              and it is the ONLY evidence that one exists. The board's filter defaults to `open`
              while a registered row is `proposed`, so even a successful registration is not
              found by opening Improvements and looking — the link to the row is the step that
              works, which is why every other propose surface renders one.
              WITHOUT an id there is no row this panel can send anyone to, so the route is the
              PULL REQUEST itself: that is what the server's warning tells the operator to do,
              and a warning whose instruction has no control beside it is the prose-only refusal
              ADR-121 D1 exists to prevent. Note that "no id" is not "no row" — the server may
              have committed the row and lost the reply that named it — which is why its wording
              distinguishes a refusal it knows created nothing from an outcome it could not
              confirm, and why this renders that wording unaltered. "Find it under Improvements" was worse than nothing here: it
              named a board the change may well not be listed on.
              THE GITHUB LINK IS A FALLBACK, NOT THE DESIGN. ADR-079 D5 is explicit that
              approving happens in Mission Control or the Fleet Console and never GitHub, and
              that owners are not expected to have GitHub access — which is why the server retries
              a transient registration failure before it ever gets here. When it still has no row
              to point at, GitHub is the only place the change exists, and naming it beats
              rendering a refusal with no control beside it. */}
          {result && !result.improvement_id && (
            <div className="mt-2 text-sm" data-testid="add-fields-publish-warning" role="alert">
              <p className="text-amber-800">
                {result.improvement_warning
                  ? result.improvement_warning
                  /* An older server answers with neither field, so nothing is known about a
                     row either way — which is itself the thing to say. */
                  : 'This version was proposed, but this Mission Control cannot tell whether it '
                    + 'was registered for in-app review, so it may not appear under '
                    + 'Improvements.'}
              </p>
              {result.url && (
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block font-medium text-blue-700 underline"
                  data-testid="add-fields-publish-pr"
                >Open the pull request on GitHub &rarr;</a>
              )}
            </div>
          )}
          {result?.improvement_id && (
            /* A plain anchor, not <Link>. SchemaBuilder's own comment records why: <Link>
               needs Router context, so it crashes anywhere the panel is rendered without one,
               and a full navigation is fine for a step taken once. */
            <a
              href={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`}
              className="mt-2 block text-sm font-medium text-blue-700 underline"
              data-testid="add-fields-publish-review"
            >Review &amp; approve in Mission Control &rarr;</a>
          )}

          {/* WHAT THE PUBLISH ACTUALLY DID TO THE CONSUMERS.
              The panel could only state the INTENT before the request: `impact_of_change`
              carries neither paths nor ownership, so a preview would have been a number that
              was wrong. This is the report, and it is the only place the operator learns which
              files moved and which did not.

              ALL FOUR KEYS ARE OPTIONAL and their ABSENCE is the signal. The server sends none
              of them when the move was declined, and an older server sends none of them at
              all — so a publish with no repin keys renders nothing here rather than an empty
              "moved nothing" that would be a claim the answer does not make. */}
          {result && repinReported && (
            <div className="mt-3 text-xs text-gray-600" data-testid="add-fields-repin-outcome">
              {result.repinned && (
                <div data-testid="add-fields-repin-moved">
                  {result.repinned.length === 0 ? (
                    /* TWO DIFFERENT EMPTY LISTS. `repinned: []` beside a populated
                       `left_behind` is the all-core-owned case, and it is common rather than
                       an edge — "nothing pinned this set" sits directly above a list of the
                       files that pin it and contradicts it. The core-owned ones are not a
                       choice the operator makes here, so this sentence reports and does not
                       ask; the route to core is in the left-behind block. */
                    result.left_behind && result.left_behind.length > 0 ? (
                      <span>
                        No consumer moved onto <code className="font-mono">{result.set}</code>.
                        This fleet cannot move the ones that pin{' '}
                        <code className="font-mono">{set}</code>, and they are listed below.
                      </span>
                    ) : (
                      <span>
                        Nothing pinned <code className="font-mono">{set}</code>, so no consumer
                        was moved.
                      </span>
                    )
                  ) : (
                    <>
                      <p>
                        Moved onto <code className="font-mono">{result.set}</code>:
                      </p>
                      <ul className="mt-0.5">
                        {result.repinned.map(rel => (
                          <li key={rel}>
                            <code className="font-mono">{rel}</code>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {/* LEFT BEHIND, WITH THE ROUTE (ADR-147 D4). Each of these is a file somebody has
                  to move by hand and this fleet may not write, so a list that stopped at the
                  refusal would be a dead end. The destination is the SAME shared hand-off the
                  out-of-scope requests above use; a second one seeded here would drift. */}
              {result.left_behind && result.left_behind.length > 0 && (
                <div className="mt-2 text-amber-800" data-testid="add-fields-repin-left-behind">
                  <p>
                    Still pinned to <code className="font-mono">{set}</code>, and this fleet
                    cannot move them:
                  </p>
                  <ul className="mt-0.5">
                    {result.left_behind.map(n => (
                      <li key={n.path}>
                        <code className="font-mono">{n.path}</code> — {n.detail || n.reason}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2">
                    <ProposeToCoreAction
                      set={set}
                      schema={schema}
                      klass={klass}
                      reason={'These consumers stayed on ' + set + ' because core owns them, so '
                        + 'only a core change can repin them.'}
                      route="Ask core to move these consumers"
                      usedBy={usedBy ?? undefined}
                    />
                  </div>
                </div>
              )}

              {/* THE LIST'S OWN LIMIT, IN THE SERVER'S WORDS. The walk reads the `/fleet`
                  mount, which lags `develop`, so a consumer added there since the mount synced
                  is missing from the list above and was not moved. Rendered as a sibling of the
                  left-behind block rather than inside it: the gap is what the list cannot see,
                  which is exactly the claim an EMPTY list makes most strongly, so a note that
                  disappeared with the block would go missing where it matters most. */}
              {result.left_behind_note && (
                <p className="mt-2 text-gray-500"
                  data-testid="add-fields-repin-left-behind-note">
                  {result.left_behind_note}
                </p>
              )}

              {result.deprecated && (
                <p className="mt-2" data-testid="add-fields-deprecated">
                  <code className="font-mono">{result.deprecated}</code> is marked{' '}
                  <code className="font-mono">deprecated</code>, which retires{' '}
                  <code className="font-mono">{set}</code> and frees its slot under the
                  live-version cap.
                </p>
              )}

              {/* A SKIP IS STATED IN WORDS, NAMING WHAT CAUSED IT. "Deprecation skipped" on its
                  own tells the operator nothing they can act on; the path is the whole of what
                  they need to go and look at. */}
              {result.deprecation_skipped && (
                <p className="mt-2 text-amber-800" data-testid="add-fields-deprecation-skipped">
                  <code className="font-mono">{set}</code> stays published:{' '}
                  {result.deprecation_skipped.detail
                    || `${result.deprecation_skipped.path} (${result.deprecation_skipped.reason})`}
                  .
                </p>
              )}
            </div>
          )}

          {/* THE BRANCH A FAILED PUBLISH LEFT BEHIND.
              `rewrite_target_changed` and `publish_incomplete` both refuse AFTER the branch
              exists. Litter nobody is told about is litter nobody cleans up, so the name is
              rendered whenever the refusal carries one — and for a partial publish, so is the
              split: a set whose consumers sit on two versions is a state the two file lists do
              not say out loud. */}
          {refusal?.branch && (
            <div className="mt-2 text-xs text-amber-800" data-testid="add-fields-publish-litter">
              <p>
                This left a branch behind: <code className="font-mono">{refusal.branch}</code>.
                Nothing was merged, so delete it once you have read it.
              </p>
              {refusal.landed && refusal.landed.length > 0 && (
                <>
                  <p className="mt-1">Already on the branch:</p>
                  <ul>
                    {refusal.landed.map(rel => (
                      <li key={rel}><code className="font-mono">{rel}</code></li>
                    ))}
                  </ul>
                </>
              )}
              {refusal.missing && refusal.missing.length > 0 && (
                <>
                  <p className="mt-1">Never reached it:</p>
                  <ul>
                    {refusal.missing.map(rel => (
                      <li key={rel}><code className="font-mono">{rel}</code></li>
                    ))}
                  </ul>
                </>
              )}
              {/* UNKNOWN IS ITS OWN LIST, AND IT SAYS SO. A commit whose acknowledgement was
                  lost and whose reconciling read could not answer is neither on the branch nor
                  absent from it. Filing it under either heading states something the server
                  does not know, so it gets its own heading and the one instruction that
                  resolves it: go and look, before the branch is deleted. */}
              {refusal.unconfirmed && refusal.unconfirmed.length > 0 && (
                <div data-testid="add-fields-publish-unconfirmed">
                  <p className="mt-1">
                    Outcome unknown — read the branch before you delete it:
                  </p>
                  <ul>
                    {refusal.unconfirmed.map(rel => (
                      <li key={rel}><code className="font-mono">{rel}</code></li>
                    ))}
                  </ul>
                </div>
              )}
              {refusal.repinPartial && (
                <p className="mt-1">
                  Some of the consumer moves landed and the rest did not, so that branch holds
                  this set split across two versions. Do not merge it.
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="mt-2 text-sm text-red-700 whitespace-pre-line" data-testid="add-fields-error">
              {error}
            </p>
          )}
          {findings && findings.length > 0 && (
            <ul className="mt-1 text-xs text-red-700" data-testid="add-fields-findings">
              {findings.map(f => <li key={f}>{f}</li>)}
            </ul>
          )}

          {/* THE EXISTING SHAPE, READ-ONLY — COLLAPSED, AND BELOW THE FORM.
              It is the longest thing on the panel (`website-property@v1` alone renders 24 rows,
              several wrapping onto two lines), and it sat above the controls, so the operator
              scrolled past the whole published contract to reach the box they came to type in.
              It stays reachable and unchanged; it is no longer in the way.

              Constraints this form cannot express are still shown rather than hidden once
              opened: an operator publishing an immutable version needs to see the contract they
              are extending. Collapsing it does not weaken the collision check either — a name
              that already exists is refused by `existingPaths` whether or not this is open. */}
          <div className="mt-6 pt-3 border-t border-gray-100" data-testid="add-fields-existing">
            <button
              type="button"
              onClick={() => setShowExisting(v => !v)}
              data-testid="add-fields-existing-toggle"
              aria-expanded={showExisting}
              className="text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
            >
              {showExisting ? '▾' : '▸'} Already published — read-only ({fields.length}{' '}
              {fields.length === 1 ? 'field' : 'fields'})
            </button>
            {showExisting && (
              <>
                <ul className="text-xs text-gray-700 mt-1">
                  {fields.map(f => (
                    <li key={f.path} data-testid={`add-fields-existing-${f.path}`} className="py-0.5">
                      <code className="font-mono">{f.path}</code>
                      <span className="ml-2 text-gray-500">{f.type}</span>
                      {f.required && <span className="ml-2 text-gray-500">· required</span>}
                      {f.description && <span className="ml-2 text-gray-400">— {f.description}</span>}
                      {f.constraints.length > 0 && (
                        <span className="ml-2" data-testid={`add-fields-constraints-${f.path}`}>
                          {f.constraints.map(c => (
                            <span key={c.key} className="ml-1 px-1 rounded bg-gray-100 text-gray-600">
                              {c.key}: {c.value}
                            </span>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-gray-400 mt-1">
                  A published version is immutable, so none of this can be changed here —
                  including the constraints this form has no control for. Adding a field is the
                  whole of what this action does.
                </p>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
